import { get, isEmpty, includes, chain, toLower, filter, size, map, compact, partition, some, reduce, camelCase, sortBy } from "lodash"

export function computeCategoryGroups(categories) {
  const { descriptors, exposure, beta, risk, predictedFactorSensitivity, historicalFactorSensitivity, contentSets, forecasts: forecastCategories } = categories
  const [proprietaryContentSets, licensedContentSets] = partition(contentSets, "proprietary")

  const descriptorsGroup = descriptors
    ? {
      id: "descriptors",
      categories: [descriptors],
    }
    : null

  const riskModelCategories = getRiskModelCategories(exposure, beta, risk)
  const areRiskModelCategoriesFound = some(riskModelCategories, "subcategoriesFound")
  const riskModel = areRiskModelCategoriesFound
    ? {
      id: "riskModel",
      label: "Risk Model",
      categories: riskModelCategories,
    }
    : null

  const areForecastCategoriesFound = some(forecastCategories, "subcategoriesFound")
  const forecasts = areForecastCategoriesFound
    ? {
      id: "forecasts",
      label: "Forecasts",
      categories: forecastCategories,
    }
    : null

  const factorSensitivityCategories = compact([
    predictedFactorSensitivity,
    historicalFactorSensitivity,
  ])

  const areFactorSensitivityCategoriesFound = some(factorSensitivityCategories, "subcategoriesFound")
  const factorSensitivity = areFactorSensitivityCategoriesFound
    ? {
      id: "factorSensitivity",
      label: "Factor Sensitivity",
      categories: factorSensitivityCategories,
    }
    : null

  const areLicensedContentSetsFound = some(licensedContentSets, "subcategoriesFound")
  const licensed = areLicensedContentSetsFound
    ? {
      id: "licensed",
      label: "Licensed",
      categories: licensedContentSets,
    }
    : null

  const areProprietaryContentSetsFound = some(proprietaryContentSets, "subcategoriesFound")
  const proprietary = areProprietaryContentSetsFound
    ? {
      id: "proprietary",
      label: "Proprietary",
      categories: proprietaryContentSets,
    }
    : null

  // TODO: Find a better way to handle descriptors or figure out which category group to put them into
  return compact([
    descriptorsGroup,
    riskModel,
    forecasts,
    factorSensitivity,
    proprietary,
    licensed,
  ])
}

function getRiskModelCategories(exposure, beta, risk) {
  return compact([
    { label: "Summary" },
    { label: "Performance" },
    exposure,
    beta,
    risk,
    { label: "Marginal Contribution" },
  ])
}

function countSelectedMetricItems(subcategory) {
  if (isEmpty(subcategory.items)) {
    return {
      ...subcategory,
      selectedItemsCount: 0,
      itemsCount: 0,
    }
  }

  const selectedItemsCount = chain(subcategory.items)
    .reduce((itemsCount, item) => {
      if (item.selected) {
        return itemsCount + 1
      }
      const selectedMetricTypeOptions = item.metricTypeOptions ? item.metricTypeOptions.filter(option => option.selected) : []
      return itemsCount + size(selectedMetricTypeOptions)
    }, 0)
    .value()

  return {
    ...subcategory,
    selectedItemsCount,
    itemsCount: size(subcategory.items),
  }
}

function makeFilteredCategory(category, searchValue) {
  return {
    ...category,
    items: filter(category.items, item => includes(toLower(item.label), toLower(searchValue))),
  }
}

export function filterAndMakeMetricSubcategories(subcategories, selectedMetricsColumns, searchValue) {
  const subcategoriesWithSelectedItems = getSubcategoriesWithSelectedItems(subcategories, selectedMetricsColumns)
  return chain(subcategoriesWithSelectedItems)
    .map(subcategory => {
      const countedSubcategory = countSelectedMetricItems(subcategory)
      if (!searchValue) {
        return { ...countedSubcategory, found: true }
      }
      if (countedSubcategory.items) {
        return makeFilteredCategory(countedSubcategory, searchValue)
      }
      if (includes(toLower(countedSubcategory.label), toLower(searchValue))) {
        return { ...countedSubcategory, found: true }
      }
      return null
    })
    .compact()
    .value()
}

export function mapMetricSubcategories(metric, selectedMetricsColumns, searchValue) {
  const filteredSubcategories = filterAndMakeMetricSubcategories(metric.columns, selectedMetricsColumns, searchValue)

  // First show subcategories without items, then sort ascending by label
  const sortedSubcategories = sortBy(filteredSubcategories, [
    ({ items }) => !isEmpty(items),
    ({ label }) => toLower(label),
  ])

  const subcategoriesFound = reduce(sortedSubcategories, (total, subcategory) =>
    total + (subcategory.found ? 1 : size(subcategory.items)), 0)

  const { subcategoriesAndItemsSelected, totalSubcategoriesAndItems } = reduce(
    sortedSubcategories,
    (acc, subcategory) => ({
      subcategoriesAndItemsSelected: acc.subcategoriesAndItemsSelected + (subcategory.selectedItemsCount || (subcategory.selected ? 1 : 0)),
      totalSubcategoriesAndItems: acc.totalSubcategoriesAndItems + (subcategory.itemsCount || 1),
    }),
    { subcategoriesAndItemsSelected: 0, totalSubcategoriesAndItems: 0 },
  )

  return {
    ...metric,
    subcategoriesFound,
    subcategoriesAndItemsSelected,
    totalSubcategoriesAndItems,
    shouldShowCategory: !!subcategoriesFound || sortedSubcategories == null,
    subcategories: sortedSubcategories,
  }
}

function getSubcategoriesWithSelectedItems(subcategories, selectedMetricsColumns) {
  return map(subcategories, (subcategory) => {
    const singleFactor = subcategory.singleFactor || !subcategory.items
    const metricType = subcategory.type
    return {
      ...subcategory,
      selected: singleFactor ? !!selectedMetricsColumns[`${metricType}.${subcategory.id}`] : false,
      singleFactor,
      items: singleFactor ? undefined : chain(subcategory.items)
        .map((item) => ({
          ...item,
          selected: !!selectedMetricsColumns[`${metricType}.${item.id}`],
          metricTypeOptions: item.metricTypeOptions ? item.metricTypeOptions.map(option => ({
            ...option,
            selected: !!selectedMetricsColumns[`${metricType}.${item.id}.${option.id}`],
          })) : null,
        }))
        .sortBy("label")
        .value(),
    }
  })
}

function getFilteredMetricsSubcategories(availableMetrics, selectedMetricsColumns, searchValue) {
  return reduce(availableMetrics, (metricsWithFilteredSubcategories, metric) => {
    if (some(metric.columns, "contentSetId")) {
      const contentSets = [...get(metricsWithFilteredSubcategories, "contentSets", []), metric]
      return {
        ...metricsWithFilteredSubcategories,
        contentSets: map(contentSets, category => mapMetricSubcategories(category, selectedMetricsColumns, searchValue)),
      }
    }
    return {
      ...metricsWithFilteredSubcategories,
      [camelCase(metric.category)]: mapMetricSubcategories(metric, selectedMetricsColumns, searchValue),
    }
  }, {})
}

export function getFilteredMetrics(availableMetrics, selectedMetricsColumns, searchValue) {
  return computeCategoryGroups(getFilteredMetricsSubcategories(availableMetrics, selectedMetricsColumns, searchValue))
}
